import { useSearchParams } from 'react-router'

export interface Pestana {
  id: string
  etiqueta: string
  /** Contador opcional que se muestra como insignia. */
  contador?: number
  render: () => React.ReactNode
}

/**
 * Pestañas de página con estado en la URL (?tab=). Permite fusionar varias
 * pantallas que comparten propósito (Gestión, Sistema) sin perder enlaces
 * directos: cada pestaña es direccionable. Solo iconos/texto, sin emojis.
 */
export function PestanasPagina({
  pestanas,
  parametro = 'tab',
  titulo,
  descripcion,
  anclaTour,
}: {
  pestanas: Pestana[]
  parametro?: string
  titulo: string
  descripcion?: string
  anclaTour?: string
}) {
  const [parametros, setParametros] = useSearchParams()
  const activa = parametros.get(parametro) ?? pestanas[0]?.id
  const actual = pestanas.find((p) => p.id === activa) ?? pestanas[0]

  const cambiar = (id: string) => {
    const copia = new URLSearchParams(parametros)
    copia.set(parametro, id)
    setParametros(copia, { replace: true })
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-tinta lg:text-2xl">{titulo}</h1>
        {descripcion ? <p className="text-sm text-tinta-apagada">{descripcion}</p> : null}
      </header>

      <div role="tablist" data-tour={anclaTour} aria-label={titulo} className="flex gap-1 border-b border-borde">
        {pestanas.map((p) => {
          const sel = p.id === actual?.id
          return (
            <button
              key={p.id}
              role="tab"
              aria-selected={sel}
              type="button"
              data-tour={anclaTour ? `${anclaTour}-${p.id}` : undefined}
              onClick={() => cambiar(p.id)}
              className={[
                'flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors',
                sel
                  ? 'border-acento text-acento'
                  : 'border-transparent text-tinta-apagada hover:text-tinta-2',
              ].join(' ')}
            >
              {p.etiqueta}
              {typeof p.contador === 'number' ? (
                <span className="rounded-full bg-elevado px-2 py-0.5 text-[11px] font-medium text-tinta-2">
                  {p.contador}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>

      <div role="tabpanel">{actual?.render()}</div>
    </div>
  )
}
